// src/components/pages/backtest/partials/PerformanceTableIndividual.js

import React, { useState } from "react";
import styles from "./style/PerformanceTableBacktest.module.css";
import { useTranslation } from "react-i18next";

const InfoIcon = () => (
  <svg 
    width="15" height="15" viewBox="0 0 24 24" 
    fill="none" stroke="currentColor" strokeWidth="2" 
    strokeLinecap="round" strokeLinejoin="round"
    style={{ color: '#9ca3af', cursor: 'help' }}
  >
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="16" x2="12" y2="12"></line>
    <line x1="12" y1="8" x2="12.01" y2="8"></line>
  </svg>
);

function PerformanceTableIndividual({ individualStocks, benchmarkMetrics, benchmarkSymbol }) {
    const [showExpertMetrics, setShowExpertMetrics] = useState(false);
    const { t } = useTranslation();

    const renderTooltip = (text) => (
        // <span className={styles.infoIconTooltipContainer}>
        //     <InfoIcon />
        //     <span className={styles.customTooltip}>{text}</span>
        // </span>
        <span className={styles.infoIconTooltipContainer}>
            <InfoIcon />
            {/* styles.tooltipBottom 클래스 추가 */}
            <span className={`${styles.customTooltip} ${styles.tooltipBottom}`}>
                {text}
            </span>
        </span>
    );

    // ✅ 모든 번역 키에 "backtest." 접두어 추가
    const explanations = {
        winRate: t("backtest.winRateExplanation"),
        profitFactor: t("backtest.profitFactorExplanation"),
        ulcer: t("backtest.ulcerExplanation"),
        upi: t("backtest.upiExplanation"),
        sharpe: t("backtest.sharpePerformancetable"),
        calmar: t("backtest.calmarPerformancetable"),
        sortino: t("backtest.sortinoPerformancetable"),
    };

    return (
        <div className={styles.performanceTableContainer}>
            <div className={styles.tableContainer}>
                <table className={styles.performanceTable}>
                    <thead>
                        <tr>
                            <th className={`${styles.stickyColumn} ${styles.stickyHeader}`}>
                                {t("backtest.symbol") || "Symbol"}
                            </th>
                            <th>{t("backtest.cumulativeReturn")}</th>
                            <th>{t("backtest.cagr")}</th>
                            <th>{t("backtest.winRate") || "Win Rate"}{renderTooltip(explanations.winRate)}</th>
                            <th>{t("backtest.profitFactor") || "PF"}{renderTooltip(explanations.profitFactor)}</th>
                            <th>{t("backtest.maxDrawdown")}</th>
                            
                            {showExpertMetrics && (
                                <>
                                    <th>{t("backtest.ulcer")} {renderTooltip(explanations.ulcer)}</th>
                                    <th>{t("backtest.upi")} {renderTooltip(explanations.upi)}</th>
                                    <th>{t("backtest.sharpeRatio")} {renderTooltip(explanations.sharpe)}</th>
                                    <th>{t("backtest.calmarRatio")} {renderTooltip(explanations.calmar)}</th>
                                    <th>{t("backtest.sortinoRatio")} {renderTooltip(explanations.sortino)}</th>
                                </>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {individualStocks.map((stock) => (
                            <tr key={stock.symbol}>
                                <td className={styles.stickyColumn}>
                                    <span className={styles.symbolBadge}>{stock.symbol}</span>
                                </td>
                                <td className={stock.metrics.cumulativeReturn > 0 ? styles.textGreen : styles.textRed}>
                                    {(stock.metrics.cumulativeReturn * 100).toFixed(2)}%
                                </td>
                                <td>{(stock.metrics.cagr * 100).toFixed(2)}%</td>
                                <td>{(stock.metrics.winRate * 100).toFixed(0)}%</td>
                                <td className={stock.metrics.profitFactor >= 1.5 ? styles.textBlue : ""}>
                                    {stock.metrics.profitFactor.toFixed(2)}
                                </td>
                                <td className={styles.textRed}>
                                    {(stock.metrics.maxDrawdown * 100).toFixed(2)}%
                                </td>
                                
                                {showExpertMetrics && (
                                    <>
                                        <td>{stock.metrics.ulcerIndex?.toFixed(2) ?? "-"}</td>
                                        <td>{stock.metrics.upi?.toFixed(2) ?? "-"}</td>
                                        <td>{stock.metrics.sharpeRatio.toFixed(2)}</td>
                                        <td>{stock.metrics.calmarRatio.toFixed(2)}</td>
                                        <td>{stock.metrics.sortinoRatio.toFixed(2)}</td>
                                    </>
                                )}
                            </tr>
                        ))}

                        {/* Benchmark Row */}
                        <tr className={styles.benchmarkRow}>
                            <td className={styles.stickyColumn} style={{ backgroundColor: "#f0f9ff" }}>
                                {t("backtest.benchmark", { benchmarkSymbol })}
                            </td>
                            <td className={styles.highlight}>
                                {(benchmarkMetrics.cumulativeReturn * 100).toFixed(2)}%
                            </td>
                            <td>{(benchmarkMetrics.cagr * 100).toFixed(2)}%</td>
                            <td>{(benchmarkMetrics.winRate * 100).toFixed(0)}%</td>
                            <td>{benchmarkMetrics.profitFactor.toFixed(2)}</td>
                            <td>{(benchmarkMetrics.maxDrawdown * 100).toFixed(2)}%</td>

                            {showExpertMetrics && (
                                <>
                                    <td>{benchmarkMetrics.ulcerIndex?.toFixed(2) ?? "-"}</td>
                                    <td>{benchmarkMetrics.upi?.toFixed(2) ?? "-"}</td>
                                    <td>{benchmarkMetrics.sharpeRatio.toFixed(2)}</td>
                                    <td>{benchmarkMetrics.calmarRatio.toFixed(2)}</td>
                                    <td>{benchmarkMetrics.sortinoRatio.toFixed(2)}</td>
                                </>
                            )}
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className={styles.toggleButtonContainer}>
                <button onClick={() => setShowExpertMetrics(!showExpertMetrics)} className={styles.expertToggleBtn}>
                    <span>{showExpertMetrics ? t("backtest.hideExpertMetrics") : t("backtest.showExpertMetrics")}</span>
                    <span style={{ fontSize: '0.8em' }}>{showExpertMetrics ? "▲" : "▼"}</span>
                </button>
            </div>
        </div>
    );
}

export default PerformanceTableIndividual;